import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import type { ColumnFiltersState } from "@tanstack/react-table";
import type { TDateRangeFilter } from "@/utils/dateRangeFilter";
import type { TServerTableQueryInput } from "@/useCases/useServerTable";
import type { TApiFileStatus } from "@/apis/proda_finance";
import {
    dateRangeParam,
    multiSelectParam,
    numberRangeParam,
    selectParam,
    sortParam,
    textParam,
} from "@/useCases/serverTableQuery";
import { mapStoreFileTypeToApi } from "@/utils/mapApiFileToStore";

const KNOWN_FILTER_COLUMNS = new Set([
    "serviceProviderName",
    "dealName",
    "fileType",
    "status",
    "period",
    "uploadedAt",
    "originalFilename",
    "lineItemCount",
]);
const DATE_FILTER_COLUMNS = new Set(["period", "uploadedAt"]);
const MULTI_SELECT_FILTER_COLUMNS = new Set(["serviceProviderName", "dealName"]);
const NUMBER_RANGE_FILTER_COLUMNS = new Set(["lineItemCount"]);
const DATE_PARAM_SEPARATOR = ",";

function serializeDateRange(value: TDateRangeFilter): string {
    return `${value.from ?? ""}${DATE_PARAM_SEPARATOR}${value.to ?? ""}`;
}

function parseDateRange(raw: string): TDateRangeFilter | undefined {
    const [from, to] = raw.split(DATE_PARAM_SEPARATOR);
    const f = from?.trim() || undefined;
    const t = to?.trim() || undefined;
    if (!f && !t) return undefined;
    return { from: f, to: t };
}

function filtersToSearchParams(
    filters: ColumnFiltersState,
    currentParams: URLSearchParams,
): URLSearchParams {
    const next = new URLSearchParams(currentParams);
    const activeIds = new Set(filters.map((f) => f.id));

    for (const key of [...next.keys()]) {
        if (KNOWN_FILTER_COLUMNS.has(key) && !activeIds.has(key)) next.delete(key);
    }

    for (const filter of filters) {
        if (filter.value === undefined || filter.value === null || filter.value === "") {
            next.delete(filter.id);
            continue;
        }
        if (MULTI_SELECT_FILTER_COLUMNS.has(filter.id)) {
            next.delete(filter.id);
            const arr = filter.value;
            if (!Array.isArray(arr) || arr.length === 0) continue;
            for (const v of arr) next.append(filter.id, String(v));
        } else if (NUMBER_RANGE_FILTER_COLUMNS.has(filter.id)) {
            // Persist numberRange as JSON so {from,to,selected} round-trips.
            next.set(filter.id, JSON.stringify(filter.value));
        } else if (DATE_FILTER_COLUMNS.has(filter.id)) {
            const serialized = serializeDateRange(filter.value as TDateRangeFilter);
            if (serialized === DATE_PARAM_SEPARATOR) next.delete(filter.id);
            else next.set(filter.id, serialized);
        } else {
            next.set(filter.id, String(filter.value));
        }
    }
    return next;
}

function searchParamsToFilters(params: URLSearchParams): ColumnFiltersState {
    const filters: ColumnFiltersState = [];
    const seen = new Set<string>();
    for (const key of params.keys()) {
        if (seen.has(key)) continue;
        seen.add(key);
        if (MULTI_SELECT_FILTER_COLUMNS.has(key)) {
            const values = params.getAll(key).filter((v) => v !== "");
            if (values.length > 0) filters.push({ id: key, value: values });
            continue;
        }
        const value = params.get(key);
        if (!value) continue;
        if (NUMBER_RANGE_FILTER_COLUMNS.has(key)) {
            try {
                filters.push({ id: key, value: JSON.parse(value) });
            } catch {
                // ignore malformed param
            }
        } else if (DATE_FILTER_COLUMNS.has(key)) {
            const range = parseDateRange(value);
            if (range) filters.push({ id: key, value: range });
        } else {
            filters.push({ id: key, value });
        }
    }
    return filters;
}

export type TFilesListingScope = {
    only_financial_journals?: boolean;
    exclude_financial_journals?: boolean;
};

export type TFilesFilterQuery = {
    skip: number;
    limit: number;
    only_financial_journals?: boolean;
    exclude_financial_journals?: boolean;
    file_type?: string;
    status?: TApiFileStatus;
    service_provider_ids?: string[];
    deal_ids?: string[];
    original_filename?: string;
    period_from?: string;
    period_to?: string;
    uploaded_from?: string;
    uploaded_to?: string;
    total_line_items_min?: string;
    total_line_items_max?: string;
    sort_by?: string;
    sort_dir?: "asc" | "desc";
};

/** Map controlled column filters + sorting + page into the files query params. */
export function filesFiltersToQuery(
    input: TServerTableQueryInput,
    scope?: TFilesListingScope,
): TFilesFilterQuery {
    const { columnFilters, sorting, page, pageSize } = input;
    const period = dateRangeParam(columnFilters, "period");
    const uploaded = dateRangeParam(columnFilters, "uploadedAt");
    const lineItems = numberRangeParam(columnFilters, "lineItemCount");
    const fileType = selectParam(columnFilters, "fileType");
    const status = selectParam(columnFilters, "status") as TApiFileStatus | undefined;
    return {
        skip: (page - 1) * pageSize,
        limit: pageSize,
        only_financial_journals: scope?.only_financial_journals,
        exclude_financial_journals: scope?.exclude_financial_journals,
        file_type: fileType ? mapStoreFileTypeToApi(fileType) : undefined,
        status,
        service_provider_ids: multiSelectParam(columnFilters, "serviceProviderName"),
        deal_ids: multiSelectParam(columnFilters, "dealName"),
        original_filename: textParam(columnFilters, "originalFilename"),
        period_from: period?.from,
        period_to: period?.to,
        uploaded_from: uploaded?.from,
        uploaded_to: uploaded?.to,
        total_line_items_min: lineItems?.min,
        total_line_items_max: lineItems?.max,
        ...sortParam(sorting),
    };
}

/**
 * URL-synced controlled filter state for the FilesTable. Column filters are the
 * single source of truth: changes flow to the URL (replace) and out via
 * `columnFilters`, which `useServerTable` consumes to drive the server fetch.
 */
export function useFilesTableFilters() {
    const [searchParams, setSearchParams] = useSearchParams();
    const initialColumnFilters = useMemo(
        () => searchParamsToFilters(searchParams),
        // Seed once on mount; later changes flow through onColumnFiltersChange.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );
    const onColumnFiltersChange = useCallback(
        (filters: ColumnFiltersState) => {
            setSearchParams((prev) => filtersToSearchParams(filters, prev), { replace: true });
        },
        [setSearchParams],
    );

    return { initialColumnFilters, onColumnFiltersChange };
}
